import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";

import {
  Avatar,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
} from "@mui/material";
import {
  AlignJustify,
  ArrowDownToLine,
  Building2,
  DollarSign,
  House,
  Library,
  Phone,
  UserRound,
} from "lucide-react";
import { useEffect, useState } from "react";

import { LoginDrawer } from "./LoginDrawer";
import { Link, LinkProps, NavLink, useLocation } from "react-router-dom";
import React from "react";
import { useAuthStore } from "../store/useAuthStore";
import CurrencySelector from "./CurrencySelector";

const LAO_TELECOM_BLUE = "#2c2f88";

type CustomProps = ListItemButtonProps & Partial<LinkProps>;

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    "& .MuiListItemIcon-root, & svg": {
      color: theme.palette.primary.contrastText,
    },
    transform: "translateX(4px)",
  },
}));

const CustomListItemButton = React.forwardRef<HTMLDivElement, CustomProps>(
  (props, ref) => {
    return <StyledListItemButton ref={ref} {...props} />;
  }
);

CustomListItemButton.displayName = "CustomListItemButton";

const CustomListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 36,
}));

const ALL_MENU_ITEMS = [
  {
    text: "Home",
    icon: <House />,
    link: "/",
    requiresAuth: false,
  },
  {
    text: "My eSims",
    icon: <Library />,
    link: "/my-esims",
    requiresAuth: true,
  },
  {
    text: "Purchase History",
    icon: <DollarSign />,
    link: "/purchase",
    requiresAuth: true,
  },
];

const MENU_SUPPORT_ITEMS = [
  {
    text: "How to Install",
    icon: <ArrowDownToLine />,
    link: "/installation-guide",
  },
  { text: "About Us", icon: <Building2 />, link: "/packages" },
  { text: "Contact Us", icon: <Phone />, link: "/contact" },
];

const MenuDrawer = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { user } = useAuthStore();

  // Filter menu items based on authentication status
  const visibleMenuItems = ALL_MENU_ITEMS.filter(
    (item) => !item.requiresAuth || user
  );

  const list = () => (
    <Box sx={{ width: 300 }} role="presentation">
      <List>
        {visibleMenuItems.map((item, _) => (
          <ListItem key={item.text} disablePadding>
            <CustomListItemButton
              component={Link}
              to={item.link}
              replace={true}
            >
              <CustomListItemIcon>{item.icon}</CustomListItemIcon>
              <ListItemText primary={item.text} />
            </CustomListItemButton>
          </ListItem>
        ))}
        <Divider variant="middle" />

        {/* {MENU_SUPPORT_ITEMS.map((item, _) => (
          <ListItem key={item.text} disablePadding>
            <CustomListItemButton component={Link} to={item.link}>
              <CustomListItemIcon>{item.icon}</CustomListItemIcon>
              <ListItemText primary={item.text} />
            </CustomListItemButton>
          </ListItem>
        ))} */}
        <Divider variant="middle" />

        {/* <ListItem disablePadding>
          <CustomListItemButton>
            <CustomListItemIcon>
              <Globe />
            </CustomListItemIcon>
            <ListItemText primary={"Language"} />
          </CustomListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <CustomListItemButton>
            <CustomListItemIcon>
              <CircleDollarSign />
            </CustomListItemIcon>
            <ListItemText primary={"LAK"} />
          </CustomListItemButton>
        </ListItem> */}
      </List>
    </Box>
  );

  return (
    <Drawer anchor={"right"} open={open} onClose={onClose}>
      {list()}
    </Drawer>
  );
};

const Navbar = () => {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);
  const [openLoginDrawer, setOpenLoginDrawer] = useState(false);
  const { user } = useAuthStore();

  const userProfilePic = user?.picture; // url
  const userName = user?.name; // name

  useEffect(() => {
    setOpenMenu(false);
    setOpenLoginDrawer(false);
  }, [location]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="fixed">
        <Toolbar sx={{ pr: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              textDecoration: "none",
              cursor: "pointer",
            }}
            component={NavLink}
            to="/"
          >
            <img
              src="laoTelecomLogo.png"
              alt="Lao Telecom logo"
              style={{ width: 44, height: "auto" }}
            />
            <Stack sx={{ ml: 1 }}>
              <Typography
                fontSize="0.95rem"
                fontWeight={700}
                color={LAO_TELECOM_BLUE}
                lineHeight={1.1}
              >
                Tourist eSim
              </Typography>
              <Typography
                fontWeight={500}
                fontSize="0.7rem"
                color={LAO_TELECOM_BLUE}
                sx={{ mt: "2px" }}
              >
                By Lao Telecom
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ mr: { xs: 0.5, sm: 1 } }}>
            <CurrencySelector />
          </Box>
          <IconButton
            color="inherit"
            aria-label="account"
            onClick={() => setOpenLoginDrawer(true)}
            sx={{ p: 0.5, mr: 0.5 }}
          >
            {userProfilePic ? (
              <Avatar
                src={userProfilePic}
                alt="Profile"
                sx={{ width: 32, height: 32 }}
              />
            ) : userName ? (
              <Avatar
                src={user?.picture}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "1rem",
                  bgcolor: "primary.main",
                }}
              >
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            ) : (
              <UserRound size={28} strokeWidth={1.5} />
            )}
          </IconButton>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={() => setOpenMenu(true)}
          >
            <AlignJustify size={28} strokeWidth={1.5} />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <MenuDrawer open={openMenu} onClose={() => setOpenMenu(false)} />
      <LoginDrawer
        open={openLoginDrawer}
        onClose={() => setOpenLoginDrawer(false)}
      />
    </Box>
  );
};

export default Navbar;
